import "./home.css";

import React from "react";
import { Link } from "react-router-dom";

import { HOME, HOME_ARTIST_ORDER, ROUTES, SITE_NAME } from "../../data/content";
import { ARTWORKS } from "../../data/artworks";
import { getArtist } from "../../data/gallery";
import ArtworkCard from "../general/ArtworkCard";
import PageMeta from "../general/PageMeta";
import Reveal from "../general/Reveal";
import Slider from "../general/Slider";

const prefersReducedMotion = () => window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

//Full-width photos that crossfade behind the title (as on Wix)
const Hero = () => {
  const [index, setIndex] = React.useState(0);

  React.useEffect(() => {
    if (prefersReducedMotion()) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      setIndex((i) => (i + 1) % HOME.HERO_SLIDES.length);
    }, HOME.HERO_SLIDE_MS);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <section className={"home-hero"}>
      {HOME.HERO_SLIDES.map((slide, i) => (
        <img
          key={slide.src}
          className={`home-hero-slide ${i === index ? "is-active" : ""}`}
          src={slide.src}
          srcSet={`${slide.small} 1440w, ${slide.src} 2880w`}
          sizes={"100vw"}
          alt={i === index ? slide.alt : ""}
          aria-hidden={i === index ? undefined : "true"}
          width={2880}
          height={2131}
          decoding={"async"}
        />
      ))}
      <h1 className={"home-hero-title"}>{HOME.HERO_TITLE}</h1>
    </section>
  );
};

//Gallery film - plays muted while it's on screen (as on Wix), with controls to unmute / pause.
//Stays paused once the visitor pauses it themselves
const GalleryVideo = () => {
  const videoRef = React.useRef(null);

  React.useEffect(() => {
    const video = videoRef.current;
    if (prefersReducedMotion() || !("IntersectionObserver" in window)) {
      return undefined;
    }
    let isAutoPausing = false;
    let wasPausedByVisitor = false;
    const onPause = () => {
      if (!isAutoPausing) {
        wasPausedByVisitor = true;
      }
      isAutoPausing = false;
    };
    const onPlay = () => {
      wasPausedByVisitor = false;
    };
    video.addEventListener("pause", onPause);
    video.addEventListener("play", onPlay);

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !wasPausedByVisitor) {
        video.muted = true;
        video.play().catch(() => {
          //Autoplay blocked (e.g. data saver) - the visitor can still press play
        });
      }
      else if (!entry.isIntersecting && !video.paused) {
        isAutoPausing = true;
        video.pause();
      }
    }, { threshold: 0.5 });
    observer.observe(video);

    return () => {
      observer.disconnect();
      video.removeEventListener("pause", onPause);
      video.removeEventListener("play", onPlay);
    };
  }, []);

  return (
    <video
      ref={videoRef}
      controls
      muted
      loop
      playsInline
      preload={"none"}
      poster={HOME.VIDEO.POSTER}
      aria-label={HOME.VIDEO.TITLE}
    >
      <source
        src={HOME.VIDEO.SRC}
        type={"video/mp4"}
      />
    </video>
  );
};

const Home = () => {
  return (
    <React.Fragment>
      <PageMeta
        title={`${SITE_NAME} | Bronze Sculpture & Ardmore Ceramics | Hyde Park Corner, Johannesburg`}
        isFullTitle
      />

      <Hero />

      <div className={"page home-page"}>
        <section
          className={"home-latest"}
          aria-labelledby={"latest-title"}
        >
          <h2
            id={"latest-title"}
            className={"section-title"}
          >
            {HOME.LATEST_TITLE}
          </h2>
          <Slider label={HOME.LATEST_TITLE}>
            {ARTWORKS.slice(0, HOME.LATEST_COUNT).map((artwork) => (
              <ArtworkCard
                key={artwork.slug}
                artwork={artwork}
                showPrice={false}
              />
            ))}
          </Slider>
        </section>

        <hr className={"rule"} />

        <section
          className={"home-about container"}
          aria-labelledby={"about-title"}
        >
          <h2
            id={"about-title"}
            className={"section-title"}
          >
            {HOME.ABOUT_TITLE}
          </h2>

          {HOME.ABOUT.map((text, i) => (
            <div
              key={text}
              className={`home-about-row ${i % 2 ? "is-reversed" : ""}`}
            >
              <div className={"home-about-text"}>
                <Reveal
                  as={"p"}
                  className={"body-text is-justified"}
                >
                  {text}
                </Reveal>
              </div>
              <Reveal
                className={"home-about-photo"}
                delay={200}
              >
                <img
                  className={"shadow-photo"}
                  src={HOME.ABOUT_IMAGES[i].src}
                  alt={HOME.ABOUT_IMAGES[i].alt}
                  loading={"lazy"}
                  width={446}
                  height={564}
                />
              </Reveal>
            </div>
          ))}

          <div className={"home-video"}>
            <Reveal className={"home-video-frame"}>
              <GalleryVideo />
            </Reveal>
          </div>

          <div className={"home-shop"}>
            <Link
              to={ROUTES.ALL_ARTWORKS}
              className={"btn"}
            >
              {HOME.SHOP_BUTTON}
            </Link>
          </div>
        </section>

        <hr className={"rule"} />

        <section
          className={"home-artists"}
          aria-labelledby={"artists-title"}
        >
          <h2
            id={"artists-title"}
            className={"section-title"}
          >
            {HOME.ARTISTS_TITLE}
          </h2>
          <Slider
            label={HOME.ARTISTS_TITLE}
            className={"home-artists-slider"}
          >
            {HOME_ARTIST_ORDER.map(getArtist).map((artist) => (
              <Link
                key={artist.slug}
                to={artist.path}
                className={"artist-tile"}
              >
                <img
                  src={artist.card}
                  alt={""}
                  loading={"lazy"}
                  width={400}
                  height={400}
                />
                <span className={"artist-tile-name"}>{artist.name}</span>
              </Link>
            ))}
          </Slider>
        </section>
      </div>
    </React.Fragment>
  );
};

export default Home;
